import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { LaboratoryApiService } from '../services/api';

export interface ReportSignOffData {
  id?: string;
  reportId: string;
  month: string; // YYYY-MM format
  signedBy: string;
  signedByName: string;
  signedAt: string;
  comments?: string;
}

interface ReportSignOffProps {
  reportId: string;
  month: string; // YYYY-MM format
}

const ReportSignOff: React.FC<ReportSignOffProps> = ({ reportId, month }) => {
  const { user } = useAuth();
  const isPortalUser = user?.email?.toLowerCase() === 'portal@clinicalsquared.com';
  const [existingSignOff, setExistingSignOff] = useState<ReportSignOffData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [comments, setComments] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Load existing sign-off — but skip entirely for the portal SSO user.
  // The portal user is read-only and the entire sign-off section is hidden
  // below; no point making the API call.
  useEffect(() => {
    if (isPortalUser) {
      setLoading(false);
      return;
    }
    const loadSignOff = async () => {
      try {
        setLoading(true);
        const signOff: ReportSignOffData = await LaboratoryApiService.getReportSignOff(reportId, month);
        setExistingSignOff(signOff);
        if (signOff && signOff.comments) {
          setComments(signOff.comments);
        }
      } catch (err: any) {
        // If 404, no sign-off exists yet - that's fine
        if (err.status !== 404) {
          console.error('Error loading sign-off:', err);
          setError('Failed to load sign-off information');
        }
      } finally {
        setLoading(false);
      }
    };

    if (reportId && month) {
      loadSignOff();
    }
  }, [reportId, month, isPortalUser]);

  // Portal SSO user is read-only — never render the sign-off section at all.
  // This is enforced server-side too (ReportSignOffController.IsPortalSsoUser
  // blocks the POST), but hiding it client-side prevents the user from even
  // seeing the affordance.
  if (isPortalUser) {
    return null;
  }

  const handleSignOff = async () => {
    if (!user) {
      setError('You must be logged in to sign off on reports');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const signOff: ReportSignOffData = {
        reportId,
        month,
        signedBy: user.id,
        signedByName: user.name || user.email || 'Unknown',
        signedAt: new Date().toISOString(),
        comments: comments.trim() || undefined,
      };

      const savedSignOff: ReportSignOffData = await LaboratoryApiService.saveReportSignOff(signOff);
      setExistingSignOff(savedSignOff);
      setShowForm(false);
    } catch (err: any) {
      console.error('Error saving sign-off:', err);
      setError(err.message || 'Failed to save sign-off');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSignOff = async () => {
    if (!user) {
      setError('You must be logged in to delete sign-offs');
      return;
    }

    const userRole = user.role?.toLowerCase();
    if (userRole !== 'admin') {
      setError('Only administrators can delete sign-offs');
      return;
    }

    // Validation: require typing "DELETE" to confirm
    if (deleteConfirmText !== 'DELETE') {
      setError('Please type "DELETE" to confirm');
      return;
    }

    try {
      setDeleting(true);
      setError(null);

      await LaboratoryApiService.deleteReportSignOff(reportId, month);
      setExistingSignOff(null);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    } catch (err: any) {
      console.error('Error deleting sign-off:', err);
      setError(err.message || 'Failed to delete sign-off');
    } finally {
      setDeleting(false);
    }
  };

  const isAdmin = user?.role?.toLowerCase() === 'admin';

  if (loading) {
    return (
      <div className="report-sign-off">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading sign-off information...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.section
      className="report-sign-off-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <h2 className="section-title">Report Sign-Off</h2>
      
      {existingSignOff && !showForm ? (
        <div className="sign-off-display">
          <div className="sign-off-info">
            <div className="sign-off-item">
              <span className="sign-off-label">Signed by:</span>
              <span className="sign-off-value">{existingSignOff.signedByName}</span>
            </div>
            <div className="sign-off-item">
              <span className="sign-off-label">Signed on:</span>
              <span className="sign-off-value">
                {new Date(existingSignOff.signedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
            {existingSignOff.comments && (
              <div className="sign-off-item">
                <span className="sign-off-label">Comments:</span>
                <span className="sign-off-value">{existingSignOff.comments}</span>
              </div>
            )}
          </div>
          <div className="sign-off-actions">
            {user && user.id === existingSignOff.signedBy && (
              <button
                className="edit-sign-off-btn"
                onClick={() => setShowForm(true)}
              >
                Update Sign-Off
              </button>
            )}
            {isAdmin && (
              <button
                className="delete-sign-off-btn"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
              >
                Clear Signature
              </button>
            )}
          </div>
          {showDeleteConfirm && (
            <div className="delete-confirm-dialog">
              <div className="delete-confirm-content">
                <h3>Clear Signature</h3>
                <p className="delete-warning">
                  Are you sure you want to clear this signature? This action cannot be undone.
                </p>
                <p className="delete-instruction">
                  Type <strong>DELETE</strong> to confirm:
                </p>
                <input
                  type="text"
                  className="delete-confirm-input"
                  value={deleteConfirmText}
                  onChange={(e) => {
                    setDeleteConfirmText(e.target.value);
                    setError(null);
                  }}
                  placeholder="Type DELETE to confirm"
                  disabled={deleting}
                />
                {error && (
                  <div className="error-message">
                    <p>{error}</p>
                  </div>
                )}
                <div className="delete-confirm-actions">
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                      setError(null);
                    }}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="confirm-delete-btn"
                    onClick={handleDeleteSignOff}
                    disabled={deleting || deleteConfirmText !== 'DELETE'}
                  >
                    {deleting ? 'Deleting...' : 'Clear Signature'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : showForm ? (
        <div className="sign-off-form">
          <div className="form-group">
            <label htmlFor="sign-off-comments" className="form-label">
              Comments (optional):
            </label>
            <textarea
              id="sign-off-comments"
              className="form-textarea"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add any comments about this report..."
              rows={4}
            />
          </div>
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}
          <div className="form-actions">
            <button
              className="cancel-btn"
              onClick={() => {
                setShowForm(false);
                setError(null);
                const currentSignOff = existingSignOff as ReportSignOffData | null;
                setComments(currentSignOff?.comments || '');
              }}
              disabled={saving}
            >
              Cancel
            </button>
            {isAdmin && existingSignOff && (
              <button
                className="delete-sign-off-btn"
                onClick={() => {
                  setShowForm(false);
                  setShowDeleteConfirm(true);
                }}
                disabled={saving}
              >
                Clear Signature
              </button>
            )}
            <button
              className="submit-sign-off-btn"
              onClick={handleSignOff}
              disabled={saving}
            >
              {saving ? 'Saving...' : existingSignOff ? 'Update Sign-Off' : 'Sign Off'}
            </button>
          </div>
        </div>
      ) : (
        <div className="sign-off-prompt">
          <p className="sign-off-message">
            This report has not been signed off yet.
          </p>
          {user && (
            <button
              className="sign-off-btn"
              onClick={() => setShowForm(true)}
            >
              Sign Off on This Report
            </button>
          )}
          {!user && (
            <p className="sign-off-message">
              Please log in to sign off on this report.
            </p>
          )}
        </div>
      )}
    </motion.section>
  );
};

export default ReportSignOff;
